import {
  announcementAudienceSchema,
  conversationSummarySchema,
  markConversationReadRequestSchema,
  messageSchema,
  messagesPageSchema,
  okSchema,
  openDirectConversationRequestSchema,
  sendAnnouncementRequestSchema,
  sendAnnouncementResponseSchema,
  sendMessageRequestSchema,
  type AnnouncementAudience,
  type ConversationSummary,
  type Message,
  type MessagesPage,
  type SendAnnouncementResponse,
} from "@teamna/shared";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * Messaging: the inbox, one thread, sending, and read marks.
 *
 * One inbox query for both sides of the app, the way the notification feed works. A conversation
 * is addressed to a person; a member who also runs a business sees their own threads and the business's
 * inbox in the same list, and each area filters to its own rows with `forBusiness` and `asStaff`.
 *
 * Polled, not pushed, and on a short leash: an open thread asks every 10 seconds, the inbox every
 * 30. Business-to-member traffic is a few messages a day per thread, and a socket for that is
 * machinery the pilot does not need yet. Sending anything invalidates both, so the sender's own
 * screen never waits on the poll.
 */

const INBOX_POLL_MS = 30 * 1000;
const THREAD_POLL_MS = 10 * 1000;
const PAGE_SIZE = 50;

/** GET /v1/me/conversations */
export function useConversations(enabled = true): UseQueryResult<ConversationSummary[]> {
  return useQuery({
    queryKey: queryKeys.conversations,
    queryFn: () => api.list("/me/conversations", conversationSummarySchema),
    enabled,
    staleTime: 10 * 1000,
    refetchInterval: INBOX_POLL_MS,
  });
}

/**
 * GET /v1/conversations/:id/messages, newest first, older pages appended as the reader scrolls up.
 *
 * The first page is re-polled while the thread is open so a reply lands without a refresh; older
 * pages never change and are left alone.
 */
export function useMessages(
  conversationId: string | null,
): UseInfiniteQueryResult<{ pages: MessagesPage[]; pageParams: unknown[] }> {
  return useInfiniteQuery({
    queryKey: queryKeys.messages(conversationId ?? ""),
    queryFn: ({ pageParam }) =>
      api.get(`/conversations/${conversationId}/messages`, messagesPageSchema, {
        query: { limit: PAGE_SIZE, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: conversationId !== null,
    refetchInterval: THREAD_POLL_MS,
    staleTime: 5 * 1000,
  });
}

/** Every loaded message, oldest first, which is the order a thread reads in. */
export function flattenMessages(pages: MessagesPage[] | undefined): Message[] {
  if (!pages) return [];
  const newestFirst = pages.flatMap((page) => page.items);
  return newestFirst.slice().reverse();
}

/** POST /v1/conversations/:id/messages */
export function useSendMessage(
  conversationId: string,
): UseMutationResult<Message, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post(
        `/conversations/${conversationId}/messages`,
        sendMessageRequestSchema.parse({ body }),
        messageSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}

/**
 * POST /v1/conversations/:id/read
 *
 * Takes the timestamp of the newest message the thread has actually rendered, never "everything",
 * so a message that arrives while the screen is open stays unread until it is seen.
 */
export function useMarkConversationRead(): UseMutationResult<
  { ok: true },
  unknown,
  { conversationId: string; through: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, through }) =>
      api.post(
        `/conversations/${conversationId}/read`,
        markConversationReadRequestSchema.parse({ through }),
        okSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}

/** POST /v1/me/businesses/:businessId/conversations/direct, the member's own thread with a business. */
export function useOpenOwnThread(): UseMutationResult<ConversationSummary, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (businessId: string) =>
      api.post(`/me/businesses/${businessId}/conversations/direct`, {}, conversationSummarySchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}

/** POST /v1/owner/businesses/:businessId/conversations/direct, the business's thread with one member. */
export function useOpenMemberThread(
  businessId: string,
): UseMutationResult<ConversationSummary, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberUserId: string) =>
      api.post(
        `/owner/businesses/${businessId}/conversations/direct`,
        openDirectConversationRequestSchema.parse({ memberUserId }),
        conversationSummarySchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}

/** GET /v1/owner/businesses/:businessId/announcements/audience */
export function useAnnouncementAudience(
  businessId: string | null,
): UseQueryResult<AnnouncementAudience> {
  return useQuery({
    queryKey: queryKeys.owner.announcementAudience(businessId ?? ""),
    queryFn: () =>
      api.get(`/owner/businesses/${businessId}/announcements/audience`, announcementAudienceSchema),
    enabled: businessId !== null,
    staleTime: 30 * 1000,
  });
}

/** POST /v1/owner/businesses/:businessId/announcements */
export function useSendAnnouncement(
  businessId: string,
): UseMutationResult<SendAnnouncementResponse, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post(
        `/owner/businesses/${businessId}/announcements`,
        sendAnnouncementRequestSchema.parse({ body }),
        sendAnnouncementResponseSchema,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(result.conversationId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}

// --- selectors ---------------------------------------------------------------------

/** The rows that belong to one business, or all of them when no business is chosen. */
export function forBusiness(
  rows: ConversationSummary[],
  businessId: string | null,
): ConversationSummary[] {
  return businessId === null ? rows : rows.filter((row) => row.businessId === businessId);
}

/**
 * The rows a member sees: threads where they are the member, plus every announcement channel.
 * A member who also runs a business keeps the business's other threads out of their personal inbox.
 */
export function asMember(rows: ConversationSummary[], userId: string | null): ConversationSummary[] {
  return rows.filter(
    (row) => row.kind === "announcement" || (userId !== null && row.memberUserId === userId),
  );
}

/** The rows the business sees: every direct thread with someone else, plus the channel. */
export function asStaff(rows: ConversationSummary[], userId: string | null): ConversationSummary[] {
  return rows.filter(
    (row) => row.kind === "announcement" || (userId !== null && row.memberUserId !== userId),
  );
}

export function totalUnread(rows: ConversationSummary[]): number {
  return rows.reduce((sum, row) => sum + row.unreadCount, 0);
}

/** The newest message on screen, which is as far as a mark-read call may reach. */
export function newestCreatedAt(items: Message[]): string | null {
  return items.reduce<string | null>(
    (newest, item) =>
      newest === null || item.createdAt.localeCompare(newest) > 0 ? item.createdAt : newest,
    null,
  );
}
